import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Wordmark } from "../../components/Logo";
import useSeo from "../../lib/useSeo";

export default function AdminLogin() {
  useSeo({ title: "Admin Login", description: "IntoLaw admin dashboard login.", path: "/admin/login" });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-[#050505] grain flex items-center justify-center px-6 font-body">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Wordmark variant="light" className="text-3xl" />
          <p className="mt-3 text-xs uppercase tracking-[0.3em] text-white/40">Admin Dashboard</p>
        </div>
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-2xl border border-white/10 bg-[#111111] p-8"
        >
          <div className="mb-6 flex items-center gap-3 text-gold">
            <Lock size={18} />
            <h1 className="font-display text-2xl font-medium text-white">Sign in</h1>
          </div>

          {error && (
            <p data-testid="login-error" className="mb-4 rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300">
              {error}
            </p>
          )}

          <label className="mb-2 block text-sm text-white/70">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email"
            className="mb-5 w-full rounded-md border border-[#262626] bg-[#050505] px-4 py-3 text-white outline-none focus:ring-1 focus:ring-gold"
          />

          <label className="mb-2 block text-sm text-white/70">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password"
            className="mb-6 w-full rounded-md border border-[#262626] bg-[#050505] px-4 py-3 text-white outline-none focus:ring-1 focus:ring-gold"
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-gold px-4 py-3 font-medium text-[#050505] transition-colors hover:bg-goldDark disabled:opacity-60"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in…</> : "Sign in"}
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-white/30">
          IntoLaw — A Division of Praxio (Pty) Ltd
        </p>
      </div>
    </div>
  );
}
